import {
    DecoratorNode,
    type DOMConversionMap,
    type DOMConversionOutput,
    type DOMExportOutput,
    type EditorConfig,
    type LexicalEditor,
    type LexicalNode,
    type NodeKey,
    type SerializedLexicalNode,
    type Spread,
    $getSelection,
    $isRangeSelection,
    $createTextNode,
    TextNode,
} from "lexical"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useEffect } from "react"
import type React from "react"

// ─── Serialized type ─────────────────────────────────────────────────────────

export type SerializedVariableNode = Spread<
    { varName: string },
    SerializedLexicalNode
>

// ─── Pill component ───────────────────────────────────────────────────────────

function VariablePill({ varName }: { varName: string }) {
    return (
        <span
            contentEditable={false}
            className="inline-flex items-center bg-brand text-white text-xs font-black px-2 py-0.5 rounded-full mx-0.5 select-none cursor-default"
        >
            {`{{${varName}}}`}
        </span>
    )
}

// ─── DecoratorNode ────────────────────────────────────────────────────────────

export class VariableNode extends DecoratorNode<React.ReactElement> {
    __varName: string

    constructor(varName: string, key?: NodeKey) {
        super(key)
        this.__varName = varName
    }

    static override getType(): string {
        return "variable"
    }

    static override clone(node: VariableNode): VariableNode {
        return new VariableNode(node.__varName, node.__key)
    }

    override createDOM(_config: EditorConfig): HTMLElement {
        const span = document.createElement("span")
        span.className = "variable-node"
        span.style.display = "inline"
        return span
    }

    override updateDOM(): boolean {
        return false
    }

    override decorate(): React.ReactElement {
        return <VariablePill varName={this.__varName} />
    }

    override exportDOM(_editor: LexicalEditor): DOMExportOutput {
        const element = document.createElement("span")
        element.setAttribute("data-variable-name", this.__varName)
        element.className = "variable-token"
        element.textContent = `{{${this.__varName}}}`
        return { element }
    }

    static override importDOM(): DOMConversionMap | null {
        return {
            span: (element: HTMLElement) => {
                if (!element.hasAttribute("data-variable-name")) return null
                return {
                    conversion: (el: HTMLElement): DOMConversionOutput => {
                        const varName = el.getAttribute("data-variable-name") ?? ""
                        return { node: new VariableNode(varName) }
                    },
                    priority: 3,
                }
            },
        }
    }

    override exportJSON(): SerializedVariableNode {
        return {
            type: "variable",
            version: 1,
            varName: this.__varName,
        }
    }

    static override importJSON(serialized: SerializedLexicalNode): VariableNode {
        const { varName } = serialized as SerializedVariableNode
        return new VariableNode(varName)
    }

    override isInline(): boolean {
        return true
    }

    override isKeyboardSelectable(): boolean {
        return true
    }
}

export function $createVariableNode(varName: string): VariableNode {
    return new VariableNode(varName)
}

// ─── Insert helper ────────────────────────────────────────────────────────────

export function insertVariable(editor: LexicalEditor, varName: string): void {
    editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
            const node = $createVariableNode(varName)
            const space = $createTextNode(" ")
            selection.insertNodes([node, space])
        }
    })
}

// ─── Auto-detect transform plugin ────────────────────────────────────────────

const VARIABLE_REGEX = /\{\{(\w+)\}\}/

export function VariableTransformPlugin(): null {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        return editor.registerNodeTransform(TextNode, (textNode: TextNode) => {
            const text = textNode.getTextContent()
            const match = VARIABLE_REGEX.exec(text)
            if (!match) return

            const varName = match[1]
            if (!varName) return
            const start = match.index
            const end = start + match[0].length

            if (start > 0) {
                // Split: [before | rest]
                const parts = textNode.splitText(start)
                const rest = parts[1]
                if (!rest) return
                // Now split rest: [varText | after]
                const restParts = rest.splitText(match[0].length)
                const varText = restParts[0]
                if (!varText) return
                const after = restParts[1]
                const varNode = $createVariableNode(varName)
                varText.replace(varNode)
                if (after) {
                    varNode.insertAfter(after)
                }
            } else {
                if (end < text.length) {
                    const parts = textNode.splitText(end)
                    const varText = parts[0]
                    const afterText = parts[1]
                    if (!varText) return
                    const varNode = $createVariableNode(varName)
                    varText.replace(varNode)
                    if (afterText) {
                        varNode.insertAfter(afterText)
                    }
                } else {
                    const varNode = $createVariableNode(varName)
                    textNode.replace(varNode)
                }
            }
        })
    }, [editor])

    return null
}
